import type { ReactNode } from "react";
import { useIsAuthViewHeightTooSmall } from "@/Shared/Hooks/useIsAuthViewHeightTooSmall";

interface AuthenticationScreenContainerProps {
  children: ReactNode;
  /** Path to the decorative side photo (e.g. "/signin-images/signin3.jpg"). */
  imageUrl: string;
}

/**
 * The shared shell for the sign-in and create-account screens: a form panel
 * beside a full-bleed photo. Below `md` it collapses to a single full-width
 * column and the photo is dropped, so the form stays usable on phones.
 */
export function AuthenticationScreenContainer({
  children,
  imageUrl,
}: AuthenticationScreenContainerProps) {
  const isViewHeightTooSmall = useIsAuthViewHeightTooSmall();
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-muted">
      {/* The card sizes itself off the viewport and is bounded on both axes, so
          neither the form nor the photo can drive it. Its height used to be a
          `min-h` floor, which handed the job to whatever was inside: the card
          jumped between wizard steps, and a portrait photo — create-account
          picks one at random from a pool that is two-thirds portrait — pushed
          it to fill the whole screen. */}
      <main className="flex min-h-screen w-full flex-col bg-card md:h-[80vh] md:max-h-[800px] md:min-h-[750px] md:w-[80vw] md:max-w-[1600px] md:flex-row md:border md:border-mid-gray">
        <section
          className={`w-full overflow-y-auto md:w-[688px] ${
            isViewHeightTooSmall
              ? "px-6 py-6 md:px-10"
              : "px-6 py-10 md:px-16 md:py-16"
          }`}
        >
          {children}
        </section>
        {/* The card's height is definite, so the photo's `h-full` resolves
            against it and `object-cover` crops whatever shape the photo is. */}
        <aside className="hidden flex-1 overflow-hidden md:block">
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover object-bottom"
          />
        </aside>
      </main>
    </div>
  );
}
